import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useEvents } from '../../contexts/EventContext';
import { useConfig } from '../../contexts/ConfigContext';
import { ROUTES } from '../../utils/constants';
import { formatDate, isDateTodayOrFuture } from '../../utils/helpers';
import { getCategoryColor, getCategoryLabel } from '../../types';

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { events, registrations, fetchEvents } = useEvents();
  const { features } = useConfig();

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const activeEvents = events.filter((e) => e.isActive);
  const upcomingEvents = activeEvents
    .filter((e) => isDateTodayOrFuture(e.date))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const totalAttendees = events.reduce((sum, e) => sum + e.currentAttendees, 0);
  const totalCapacity = events.reduce((sum, e) => sum + e.capacity, 0);
  const confirmedRegistrations = registrations.filter((r) => r.status === 'confirmed').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage Team Jak events and registrations
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {features.qrCheckIn && (
            <Button
              variant="outlined"
              startIcon={<QrCodeScannerIcon />}
              onClick={() => navigate(ROUTES.ADMIN_QR_CHECK_IN)}
            >
              QR Check-In
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate(ROUTES.ADMIN_CREATE_EVENT)}
          >
            Create Event
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #00b7ed 0%, #0095c4 100%)',
              color: 'white',
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <EventIcon fontSize="small" />
                <Typography variant="caption">Active Events</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {activeEvents.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: 'white',
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <PeopleIcon fontSize="small" />
                <Typography variant="caption">Total Registrations</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {confirmedRegistrations}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: 'white',
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <TrendingUpIcon fontSize="small" />
                <Typography variant="caption">Total Attendees</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {totalAttendees}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              color: 'white',
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <EventIcon fontSize="small" />
                <Typography variant="caption">Capacity Utilization</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {totalCapacity > 0 ? Math.round((totalAttendees / totalCapacity) * 100) : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Upcoming Events */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Upcoming Events
                </Typography>
                <Button size="small" onClick={() => navigate(ROUTES.ADMIN_EVENTS)}>
                  View All
                </Button>
              </Box>

              {upcomingEvents.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No upcoming events
                </Typography>
              ) : (
                <List disablePadding>
                  {upcomingEvents.slice(0, 5).map((event) => (
                    <ListItem
                      key={event.id}
                      sx={{
                        px: 0,
                        borderBottom: '1px solid #e0e0e0',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <ListItemText
                        primary={event.title}
                        secondary={formatDate(event.date)}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={getCategoryLabel(event.category)}
                          size="small"
                          sx={{
                            backgroundColor: getCategoryColor(event.category),
                            color: 'white',
                            mr: 1,
                          }}
                        />
                        <Chip
                          label={`${event.currentAttendees}/${event.capacity}`}
                          size="small"
                          variant="outlined"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Quick Actions
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<AddCircleIcon />}
                    onClick={() => navigate(ROUTES.ADMIN_CREATE_EVENT)}
                    sx={{ py: 2 }}
                  >
                    Create Event
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<EventIcon />}
                    onClick={() => navigate(ROUTES.ADMIN_EVENTS)}
                    sx={{ py: 2 }}
                  >
                    Manage Events
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PeopleIcon />}
                    onClick={() => navigate(ROUTES.ADMIN_ATTENDEES)}
                    sx={{ py: 2 }}
                  >
                    View Attendees
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate(ROUTES.EVENTS)}
                    sx={{ py: 2 }}
                  >
                    Public View
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Events by Category */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Events by Category
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {['workshop', 'fundraiser', 'social', 'family-day', 'volunteering'].map(
                  (category) => {
                    const count = events.filter((e) => e.category === category).length;
                    return (
                      <Chip
                        key={category}
                        label={`${getCategoryLabel(category as any)}: ${count}`}
                        sx={{
                          backgroundColor: getCategoryColor(category as any),
                          color: 'white',
                        }}
                      />
                    );
                  }
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboardPage;
